/**
 * Warden — Advisory practice.
 *
 * Advisory pages are the easiest place to overclaim, so these say plainly what
 * the fractional and briefing engagements are not: not a legal opinion, and not
 * an accountable regulatory officer.
 */

import type { ServicePageContent } from "./types";

const PRACTICE = "Advisory";

const ADVISORY: ServicePageContent = {
  path: "/warden/advisory",
  practice: PRACTICE,
  title: "AI Governance Advisory",
  summary:
    "Standing access to the people doing this work, for decisions that will not wait.",
  headline: "Most AI governance decisions are made in a meeting, not in a project",
  lede:
    "A retained advisory relationship: the team behind Cerberus, Argus and TraceLock available for the architecture reviews, vendor questions and go/no-go calls that arrive between engagements.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=advisory" },
    { label: "Govern AI Adoption", href: "/govern-ai" },
  ],
  scope: {
    heading: "The questions do not arrive on a project schedule",
    body:
      "A team proposes an agent with production database access on Tuesday and wants an answer by Thursday. Scoping an engagement takes longer than the decision does, so the decision gets made without the input.",
    points: [
      {
        title: "Architecture review is the highest-leverage moment",
        body: "Changing a design before it ships costs a conversation; changing it afterwards costs a quarter.",
      },
      {
        title: "Vendor claims need a technical reader",
        body: "AI security marketing is largely unfalsifiable, and telling the difference requires having built the thing.",
      },
      {
        title: "Precedent compounds",
        body: "The first agent approved sets the standard every subsequent team will point at.",
      },
    ],
  },
  method: {
    heading: "How the retainer runs",
    steps: [
      {
        title: "Context load",
        body: "We learn your estate, architecture and risk appetite up front so advice is specific rather than generic.",
      },
      {
        title: "Standing availability",
        body: "A named advisor and an agreed response time, reachable through your channels.",
      },
      {
        title: "Scheduled reviews",
        body: "A recurring session for architecture decisions, vendor evaluations and roadmap questions.",
      },
      {
        title: "Written positions",
        body: "Where a decision needs a record, a short written recommendation with reasoning you can circulate.",
      },
    ],
  },
  deliverables: {
    heading: "What the retainer includes",
    items: [
      "A named advisor with context on your estate",
      "An agreed response time for advisory requests",
      "Recurring architecture and design review sessions",
      "Written recommendations for decisions that need a record",
      "Vendor and tooling evaluation support",
      "Advisory only — this is not legal advice",
    ],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Virtual AI Risk Officer", href: "/warden/virtual-airo" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
  close: {
    heading: "Available when the decision is being made",
    body:
      "Advice after the fact is a post-mortem. The value is in being reachable on Tuesday.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=advisory" },
      { label: "Govern AI Adoption", href: "/govern-ai" },
    ],
  },
};

const VIRTUAL_AIRO: ServicePageContent = {
  path: "/warden/virtual-airo",
  practice: PRACTICE,
  title: "Virtual AI Risk Officer",
  summary:
    "A fractional AI risk function for organizations not ready to hire one.",
  headline: "You need the function long before you can justify the headcount",
  lede:
    "A fractional AI risk officer embedded in your organization — chairing the governance forum, owning the AI risk register, and preparing what the board and your regulators ask for.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=virtual-airo" },
    { label: "AI Governance Advisory", href: "/warden/advisory" },
  ],
  scope: {
    heading: "The role exists whether or not anyone holds it",
    body:
      "Someone has to decide which AI use cases proceed, maintain the risk register, and answer the board. In most organizations that work is distributed across people who each have a different day job.",
    points: [
      {
        title: "Distributed ownership means no ownership",
        body: "When AI risk is everyone's secondary responsibility, the register is maintained by nobody.",
      },
      {
        title: "The market for the skill is thin",
        body: "People who understand both AI systems and regulatory obligation are scarce and expensive to hire.",
      },
      {
        title: "Regulators expect a named individual",
        body: "Supervisory conversations go better when there is one person who owns the answer.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Establish the function",
        body: "Terms of reference, decision rights, escalation path and reporting line, agreed in writing.",
      },
      {
        title: "Operate it",
        body: "Chair the governance forum, run intake for new AI use cases, and maintain the risk register.",
      },
      {
        title: "Report",
        body: "Board and committee reporting on AI risk posture, exceptions and material changes.",
      },
      {
        title: "Build the succession",
        body: "Document the function so a permanent hire inherits a working practice rather than a blank page.",
      },
    ],
  },
  deliverables: {
    heading: "What the engagement includes",
    items: [
      "A named fractional AI risk officer, with agreed days per month",
      "A chaired AI governance forum with recorded decisions",
      "A maintained AI risk register with owners and treatment plans",
      "Use-case intake and approval process, operated rather than just designed",
      "Board and committee reporting",
      "A documented handover pack for the eventual permanent hire",
      "Fractional and advisory: accountability for the function remains with your organization, and we do not hold a regulated officer position on your behalf",
    ],
  },
  drivers: {
    heading: "Why the role is appearing now",
    items: [
      {
        title: "EU AI Act",
        body: "Regulation (EU) 2024/1689 places per-system obligations that need an owner inside the organization.",
      },
      {
        title: "ISO/IEC 42001:2023",
        body: "Certification requires defined roles, responsibilities and management review — a function, not a project.",
      },
      {
        title: "Customer due diligence",
        body: "Enterprise buyers increasingly ask who owns AI risk before they will sign.",
      },
    ],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "AI Governance Advisory", href: "/warden/advisory" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
    ],
  },
  close: {
    heading: "The function now, the hire later",
    body:
      "And when you do hire, they inherit a running governance forum and a maintained register instead of starting over.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=virtual-airo" },
      { label: "Govern AI Adoption", href: "/govern-ai" },
    ],
  },
};

const BRIEFINGS: ServicePageContent = {
  path: "/warden/briefings",
  practice: PRACTICE,
  title: "Board & Regulator Briefings",
  summary:
    "Prepare the AI risk conversation for the audiences that will not accept hand-waving.",
  headline: "The board question is not what AI can do, it is what happens when it is wrong",
  lede:
    "Preparation and delivery support for AI risk conversations with boards, audit committees, regulators and enterprise customers — grounded in your actual posture rather than in reassurance.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=briefings" },
    { label: "AI Governance Advisory", href: "/warden/advisory" },
  ],
  scope: {
    heading: "Technical accuracy and board legibility pull in opposite directions",
    body:
      "Material written by the engineering team is accurate and unreadable; material written for the board is readable and frequently overstates the position. Both fail the moment a director asks a follow-up question.",
    points: [
      {
        title: "Directors ask about consequence",
        body: "Not how the control works, but what happens if it does not, and who finds out first.",
      },
      {
        title: "Overstatement is a governance risk",
        body: "A reassuring slide that later proves wrong is worse for the organization than the original exposure.",
      },
      {
        title: "Supervisors probe the qualifier",
        body: "A regulator will ask what a number was measured under, and the answer needs to be ready.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Establish the real position",
        body: "What can be evidenced, what is in progress, and what is genuinely unknown.",
      },
      {
        title: "Build the material",
        body: "Written for the audience, with every claim traceable to evidence held.",
      },
      {
        title: "Rehearse the questions",
        body: "The hard follow-ups, and answers that stay accurate under pressure.",
      },
      {
        title: "Support delivery",
        body: "Attend where useful, to answer the technical question the board will inevitably ask.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A board or committee pack on AI risk posture, with every claim traceable to evidence",
      "A rehearsed question bank covering the hard follow-ups",
      "Regulator-facing material appropriate to your supervisory relationship",
      "A customer-facing summary for enterprise due diligence",
      "Optional attendance to answer technical questions in the room",
    ],
  },
  drivers: {
    heading: "What the audience is working from",
    items: [
      {
        title: "SEC cybersecurity disclosure",
        body: "Form 8-K Item 1.05 and Regulation S-K Item 106 have made board oversight of cyber risk an explicit disclosure matter.",
      },
      {
        title: "EU AI Act",
        body: "Regulation (EU) 2024/1689 gives boards a concrete compliance deadline to ask about.",
      },
      {
        title: "Customer due diligence",
        body: "Enterprise buyers now ask AI-specific questions in security review, and the answers become contractual.",
      },
    ],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Virtual AI Risk Officer", href: "/warden/virtual-airo" },
      { label: "AI Governance Advisory", href: "/warden/advisory" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
    ],
  },
  close: {
    heading: "Say what is true, and be ready for the follow-up",
    body:
      "A board that trusts the reporting will fund the program. One that catches an overstatement will audit it instead.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=briefings" },
      { label: "Prove AI Compliance", href: "/ai-compliance" },
    ],
  },
};

const POLICY_PROGRAM: ServicePageContent = {
  path: "/warden/policy-program",
  practice: PRACTICE,
  title: "Policy Program Design",
  summary:
    "Write AI policy people can follow, and prove they acknowledged it.",
  headline: "An AI policy nobody can follow produces shadow AI, not compliance",
  lede:
    "Design and rollout of an AI policy program: acceptable use, approval routes for new AI systems, and the acknowledgement record that turns a document into a control.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=policy-program" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "Prohibition policies get routed around",
    body:
      "A policy that bans AI tools without offering an approved path does not stop usage; it moves it onto personal accounts where you cannot see it, which is strictly worse than the thing it was written to prevent.",
    points: [
      {
        title: "No approved path means no visibility",
        body: "People will use the tool that does their job and will not tell you about it.",
      },
      {
        title: "Approval routes that take weeks are ignored",
        body: "If the process is slower than the deadline, the process loses.",
      },
      {
        title: "Unacknowledged policy is not evidence",
        body: "A document in a repository nobody has read will not support an enforcement decision.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Understand actual usage",
        body: "What people are doing with AI today, so the policy addresses reality rather than an assumption.",
      },
      {
        title: "Draft against a framework",
        body: "Acceptable use, data handling and approval routes, mapped to ISO/IEC 42001 and NIST AI RMF so it counts as control evidence.",
      },
      {
        title: "Design the approved path",
        body: "A route to getting a new AI use case approved that is fast enough that people use it.",
      },
      {
        title: "Roll out and record",
        body: "Publish, campaign for acknowledgement, and keep the record of who accepted which version and when.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "An AI acceptable use policy written against your actual usage patterns",
      "Framework mapping so the policy stands as control evidence",
      "A documented approval route for new AI use cases, with a service level",
      "An acknowledgement campaign and a per-version record of who accepted what",
      "A review cadence so the policy tracks the tooling rather than expiring",
    ],
  },
  products: {
    heading: "What runs it",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
      { label: "Enablement & Training", href: "/warden/enablement" },
      { label: "Virtual AI Risk Officer", href: "/warden/virtual-airo" },
    ],
  },
  close: {
    heading: "A policy with a path and a record",
    body:
      "Those two things are the difference between a governance control and a document that exists to be shown to auditors.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=policy-program" },
      { label: "Explore TraceLock", href: "/tracelock" },
    ],
  },
};

export const ADVISORY_SERVICES: ServicePageContent[] = [
  ADVISORY,
  VIRTUAL_AIRO,
  BRIEFINGS,
  POLICY_PROGRAM,
];
